import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, poundsFromPence, formatErr } from "../lib/api";
import { PublicShell } from "./PublicLandingPage";
import { mergeBranding } from "../lib/templates";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { getStripe } from "../lib/stripe";
import { Clock, CheckCircle2, AlertTriangle } from "lucide-react";

function fmtDate(ds) {
  if (!ds) return "";
  const [y, m, d] = ds.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function stripeAppearance(b) {
  return {
    theme: "night",
    variables: {
      colorPrimary: b.btn_bg || b.accent_color || "#dc2626",
      colorBackground: b.panel_bg || "#0e0e0e",
      colorText: b.text_color || "#ffffff",
      borderRadius: `${b.btn_border_radius != null ? b.btn_border_radius : 6}px`,
    },
  };
}

export default function PublicPaymentPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [pi, setPi] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/pay/${token}`); setData(r.data); }
      catch { setErr("This payment link could not be found."); }
    })();
  }, [token]);

  const attraction = data ? { name: data.attraction.name, slug: data.attraction.slug, branding: data.attraction.branding } : null;

  if (err) return <PublicShell><Centered icon={<AlertTriangle size={40} />} title="Link not found" body={err} /></PublicShell>;
  if (!data) return <PublicShell><div className="text-center py-32 text-[#8a8a8a]">Loading…</div></PublicShell>;

  const b = mergeBranding(data.attraction.branding);
  const connected = data.payments_enabled && data.attraction.stripe_onboarding_complete;

  if (data.status === "paid") {
    return <PublicShell attraction={attraction}><Centered icon={<CheckCircle2 size={40} />} title="Already paid"
      body="This payment has already been completed. Thank you." testid="pay-paid" /></PublicShell>;
  }
  if (data.status === "expired" || data.status === "cancelled") {
    const cancelled = data.status === "cancelled";
    return <PublicShell attraction={attraction}><Centered icon={<Clock size={40} />}
      title={cancelled ? "This payment link has been cancelled" : "This payment link has expired"}
      body={`Please contact ${data.attraction.name} for assistance.`} testid="pay-expired" /></PublicShell>;
  }

  const startPayment = async () => {
    setErr(""); setStarting(true);
    try {
      const r = await api.post(`/pay/${token}/create-payment-intent`);
      setPi({ clientSecret: r.data.client_secret, publishableKey: r.data.publishable_key, stripeAccount: r.data.stripe_account, orderId: r.data.order_id });
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Could not start payment."); }
    finally { setStarting(false); }
  };

  const timeText = data.order.time_start ? `${data.order.time_start}${data.order.time_end ? "–" + data.order.time_end : ""}` : null;

  return (
    <PublicShell attraction={attraction}>
      <div className="max-w-lg mx-auto px-6 py-16 st-fade-up" data-testid="public-payment-page">
        <h1 className="text-3xl font-bold text-center">Complete your booking</h1>
        <p className="text-center text-[#bbb] mt-2">{data.attraction.name}</p>

        <div className="st-card p-6 mt-8">
          <div className="text-sm space-y-2">
            <Row label="Booking" value={`#${data.order.order_number}`} mono />
            {data.order.visit_date && <Row label="Visit date" value={fmtDate(data.order.visit_date)} />}
            {timeText && <Row label="Time slot" value={timeText} />}
          </div>
          <div className="st-divider my-4" />
          <div className="text-sm text-[#dadada]" data-testid="pay-description">{data.description}</div>
          <div className="flex justify-between items-baseline mt-4">
            <span className="text-sm uppercase tracking-wider text-[#8a8a8a]">Amount due</span>
            <span className="text-2xl font-bold" data-testid="pay-amount">{poundsFromPence(data.amount_pence)}</span>
          </div>

          {err && <div className="text-sm text-[#fca5a5] mt-4" data-testid="pay-error">{err}</div>}

          {!connected ? (
            <div className="rounded-lg border border-[#3a2a0a] bg-[#1a1205] p-4 text-sm text-[#e8c987] mt-5" data-testid="pay-unavailable">
              Online payment is temporarily unavailable. Please contact {data.attraction.name}.
            </div>
          ) : pi ? (
            <div className="mt-5">
              <Elements stripe={getStripe(pi.publishableKey, pi.stripeAccount)} options={{ clientSecret: pi.clientSecret, appearance: stripeAppearance(b) }}>
                <PayInner amount={poundsFromPence(data.amount_pence)}
                  returnUrl={`${window.location.origin}/${data.attraction.slug}/confirmation/${pi.orderId}`}
                  onError={(m) => setErr(m)} />
              </Elements>
            </div>
          ) : (
            <button className="st-btn mt-5 w-full justify-center" disabled={starting} onClick={startPayment} data-testid="pay-start">
              {starting ? "Loading…" : `Pay ${poundsFromPence(data.amount_pence)}`}
            </button>
          )}
        </div>
        <p className="text-center text-xs text-[#7a7a7a] mt-4">Secured by Stripe. {data.expires_at ? `Link valid until ${new Date(data.expires_at).toLocaleString("en-GB")}.` : ""}</p>
      </div>
    </PublicShell>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between">
      <span className="text-[#8a8a8a]">{label}</span>
      <span className={mono ? "st-mono" : ""}>{value}</span>
    </div>
  );
}

function Centered({ icon, title, body, testid }) {
  return (
    <div className="text-center py-32 px-6" data-testid={testid}>
      <div className="inline-flex w-16 h-16 rounded-full bg-[#1a1a1a] items-center justify-center text-[#dc2626]">{icon}</div>
      <h1 className="text-2xl font-bold mt-5">{title}</h1>
      <p className="text-[#bbb] mt-2 max-w-sm mx-auto">{body}</p>
    </div>
  );
}

function PayInner({ amount, returnUrl, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    const { error } = await stripe.confirmPayment({ elements, confirmParams: { return_url: returnUrl } });
    if (error) { onError(error.message || "Payment could not be completed."); setBusy(false); }
  };
  return (
    <div data-testid="pay-stripe">
      <PaymentElement options={{ disableLink: true }} />
      <button className="st-btn mt-5 w-full justify-center" disabled={!stripe || busy} onClick={pay} data-testid="pay-confirm">
        {busy ? "Processing…" : `Pay ${amount}`}
      </button>
    </div>
  );
}
